import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import styles from './pieDiagram.module.css';
import { getAll } from 'utils/queries';

// Utility function to generate dynamic colors
const generateColors = (count) => {
  // Option 1: Pre-defined color palette that cycles if needed
  const colorPalette = [
    '#B06EC2', '#C4D869', '#5CD4B9', '#F47B67', '#7BA3DD', 
    '#FFA07A', '#20B2AA', '#778899', '#B0C4DE', '#FFDEAD',
    '#BC8F8F', '#4682B4', '#87CEFA', '#40E0D0', '#9370DB'
  ];
  
  // If we have more data points than colors, cycle through the palette
  return Array.from({ length: count }, (_, i) => colorPalette[i % colorPalette.length]);
  
  // Option 2: Completely dynamic color generation (alternative approach)
  // return Array.from({ length: count }, (_, i) => {
  //   const hue = (i * 137.5) % 360; // Use golden angle approximation for good distribution
  //   return `hsl(${hue}, 70%, 65%)`; // Maintaining decent saturation and lightness
  // });
};

const PieChart = ({ data }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    
    // Generate colors dynamically based on the number of data points
    const dynamicColors = generateColors(data.length);

    const chartData = {
      labels: data.map(item => item.ticket_name),
      datasets: [{
        data: data.map(item => Number(item.orders)),
        backgroundColor: dynamicColors,
        borderWidth: 0,
      }]
    };

    chartInstance.current = new Chart(ctx, {
      type: 'pie',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          legend: {
            display: true,
            position: 'left',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 20,
              font: { size: 12 }
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const label = context.label || '';
                const value = context.formattedValue;
                return `${label}: ${value}`;
              }
            }
          }
        },
        layout: { padding: 10 }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  return (
    <div className={styles.piechartbox}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
};

const CustomTable = ({ columns, data }) => {
  // Generate colors for the dot indicators
  const dotColors = generateColors(data.length);
  
  return (
    <div className={styles.customtablewrapper}>
      <table className={styles.customtable}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index}>{column.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column, colIndex) => (
                <td key={colIndex}>
                  {column.selector(row)}
                  {column.name === 'Ticket' && (
                    <span 
                      className={styles.ticketcolordot}
                      style={{ backgroundColor: dotColors[rowIndex] }}
                    ></span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BreakdownByTicket = ({ startDate, endDate, dateLabel, eventId }) => {
  const [ticketData, setTicketData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchTicketSalesBreakDown = async ({ limit = 10, offset = 0 }) => {
    // Only proceed if we have both start and end dates
    if (!startDate || !endDate) {
      return;
    }

    setIsLoading(true);
    try {
      // Format dates for API
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      // Include date and event parameters in the API call
      let url = `orders/metrics/ticketsales?date=${formattedStartDate}&endDate=${formattedEndDate}&limit=${limit}&offset=${offset}`;
      
      if (eventId) {
        url += `&event_id=${eventId}`;
      }

      const result = await getAll(url);
      
      const data = result?.data?.data;
      if (Array.isArray(data)) {
        setTicketData(data);
      }
    } catch (error) {
      console.error("Failed to fetch ticket sales breakdown:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when start, end date or eventId changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchTicketSalesBreakDown({});
    }
  }, [startDate, endDate, eventId]);

  const columns = [
    {
      name: 'Ticket',
      selector: (row) => row.ticket_name,
    },
    {
      name: 'Tickets Sold',
      selector: (row) => row.orders,
    },
    {
      name: 'Sales',
      selector: (row) =>`£ ${Number(row.sales).toFixed(2)}`,
    },
    {
      name: 'AOV',
      selector: (row) => `£ ${row.aov}`,
    },
  ];

  return (
    <div className={styles.breakdowncontainer}>
      <div className={styles.breakdownheader}>
        <h2 className={styles.breakdowntitle}>Breakdown by Ticket</h2>
        {dateLabel && <span className={styles.datelabel}>{dateLabel}</span>}
      </div>
      {isLoading ? (
        <div className={styles.loadingState}>Loading ticket data...</div>
      ) : ticketData.length > 0 ? (
        <div className={styles.breakdowncontent}>
          <div className={styles.tablecontainer}>
            <CustomTable columns={columns} data={ticketData} />
          </div>
          <div className={styles.chartcontainer}>
            <PieChart data={ticketData} />
          </div>
        </div>
      ) : (
        <div className={styles.emptyState}>No ticket data available for the selected date range.</div>
      )}
    </div>
  );
};

export default BreakdownByTicket;